import { StatsGl } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { RefObject, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useMap } from 'react-three-map';
import {
  DebugThreeGlInfo,
  modelManager,
  checkModelExists,
  getCachedModelExistence,
  configureGLTFDecoders,
} from '@repo/ui';
import React from 'react';

// Types
import { TwinEntity } from '@repo/backend-types';
import type { CameraSetup, MapLocationAndCamera } from '@repo/frontend-types';
import Config from '../../common/Config';
import { TwinEntity3D } from './TwinEntity3D';
import {
  PLAN_VIEW_OUTSIDE,
  PlanHeightGroup,
  PlanViewContext,
  usePlanView,
} from './planView/PlanHeightScale';
import { LiveEditFlightBridge } from './planView/LiveEditFlightBridge';
import { liveEditFlightScene, setLiveAnchorOrigin } from './planView/liveEditFlightScene';
import {
  MachineLookLights,
  MachineLookPostProcessChain,
  MachineLookScope,
} from './planView/MachineLook';
import { useGLTF } from '@react-three/drei';

// Context / Stores
import { useDeveloperStore, ConfigurationSource, useTwinContext } from '@repo/front-end-state';
import { EQUIPMENT_PHASES, currentEquipmentLoadToken, markEnd } from '@repo/front-end-utils';

// Interface props for ThreeScene component
interface Props {
  cameraStart: CameraSetup | null;
  twinRootEntity: TwinEntity;
  infoRef: RefObject<HTMLDivElement | null>;
  onAllModelsLoaded: () => void;
  mapOrigin: { latitude: number; longitude: number };
  resolvedCamera?: MapLocationAndCamera;
}

const DEFAULT_CAMERA_CONFIG = {
  zoom: 19,
  bearing: 340,
  pitch: 70,
};

const MODEL_COMPLETION_DELAY_MS = 500;

/**
 * Computes a fingerprint/hash of the entity tree's relevant properties in a single traversal.
 * This is used for efficient comparison in React.memo to avoid multiple expensive tree traversals.
 *
 * The fingerprint includes:
 * - modelFile values (sorted)
 * - rotationY values (sorted by bID)
 * - name and capacity values (sorted by bID)
 *
 * Returns a string that uniquely represents the state of these properties across the entire tree.
 * For large entity trees, this single-pass approach is significantly more efficient than
 * multiple separate traversals.
 */
function computeEntityTreeFingerprint(entity: TwinEntity): string {
  const entities: Array<{
    bID: string;
    modelFile: string | null | undefined;
    rotationY: number | null | undefined;
    name: string | null | undefined;
    capacity: number | null | undefined;
  }> = [];
  const stack: TwinEntity[] = [entity];

  // Single traversal to collect all relevant data
  while (stack.length > 0) {
    const current = stack.pop();
    if (!current) continue;

    entities.push({
      bID: current.bID || current.id || '',
      modelFile: current.modelFile,
      rotationY: current.rotationY,
      name: current.name,
      capacity: current.capacity,
    });

    if (current.children && current.children.length > 0) {
      stack.push(...current.children);
    }
  }

  // Sort by bID for consistent ordering
  entities.sort((a, b) => (a.bID || '').localeCompare(b.bID || ''));

  // Create fingerprint string from sorted data
  // Format: "bID|modelFile|rotationY|name|capacity||bID|modelFile|..."
  const fingerprint = entities
    .map(
      e => `${e.bID}|${e.modelFile ?? ''}|${e.rotationY ?? ''}|${e.name ?? ''}|${e.capacity ?? ''}`,
    )
    .join('||');

  return fingerprint;
}

// Background preloader that warms drei's cache for all model URLs once per twin
const PreloadAllModels: React.FC = () => {
  useEffect(() => {
    // One-time decoder config for faster glTF parsing
    configureGLTFDecoders();

    const prioritized = modelManager.getPrioritizedPreloadList();
    const urls = prioritized.map(p => p.url);
    const CONCURRENCY = 8;
    let index = 0;
    let cancelled = false;

    const worker = async () => {
      while (!cancelled && index < urls.length) {
        const url = urls[index++];
        const cached = getCachedModelExistence(url);
        const exists = cached !== undefined ? cached : await checkModelExists(url);
        if (exists) {
          try {
            useGLTF.preload(url);
          } catch (_) {}
        }
      }
    };

    const workers = Array.from({ length: Math.min(CONCURRENCY, urls.length) }, () => worker());
    Promise.all(workers).catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);
  return null;
};

// A scene to render a twin - Memoized to prevent re-renders on mode changes
const TwinScene = React.memo(
  ({
    twinRootEntity: topLevelEntity,
    mapOrigin,
    infoRef,
    onAllModelsLoaded,
    cameraStart,
    resolvedCamera,
  }: Props) => {
    // Diagnostic: log TwinScene mount/unmount. Pairs with [MapCanvas render]
    // and [TwinScene frames] logs to trace whether the React tree unmounts
    // TwinScene before/during a fly-to-org. See plan-fly-to-target.md CP4a.
    useEffect(() => {
      // eslint-disable-next-line no-console
      console.debug('[TwinScene] mount, twinId=', topLevelEntity?.id);
      return () => {
        // eslint-disable-next-line no-console
        console.debug('[TwinScene] unmount, twinId=', topLevelEntity?.id);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // `equipment:scene-render` — closed on the first R3F frame after an Equipment load opens
    // (equipment-performance R2). Rob reports the panel AND the scene as slow and they may have
    // different causes, so this closes independently of the panel's mark.
    //
    // `useFrame` rather than `requestAnimationFrame`: this fires inside R3F's own render loop, so it
    // is a genuine "the scene has been drawn" signal. A rAF callback can run in a frame where R3F
    // painted nothing — and during a flyTo the frameloop can be frozen entirely (see the frame
    // counter below), which a rAF-based mark would silently report as a fast scene.
    //
    // LATCHED ON THE LOAD TOKEN, not the twin BID. Two bugs fixed at once:
    //  - This component is NOT unmounted on a mode switch, so a twin-keyed ref survived leaving and
    //    re-entering Equipment mode and permanently suppressed the mark. Since `equipment:total`
    //    closes only when BOTH panel and scene are done, a WARM re-entry emitted no total at all —
    //    exactly the case AC8's warm baseline needs.
    //  - Reading `appMode` from `useModeContext()` subscribed this component to a context, which
    //    bypasses the `React.memo` comparator below whose entire purpose is to keep mode and calendar
    //    changes from re-rendering the 3D scene. The load token needs no subscription: it only ever
    //    advances on Equipment-mode entry, so it already carries the "we are in an Equipment load"
    //    signal, and reading it inside `useFrame` happens outside React's render cycle.
    const sceneMarkedFor = useRef<number | undefined>(undefined);
    useFrame(() => {
      const loadToken = currentEquipmentLoadToken();
      if (loadToken === 0 || sceneMarkedFor.current === loadToken) return;

      // Latch ONLY on a mark that actually fired. `markEnd` returns undefined while the phase has not
      // started — i.e. until the Equipment data has landed — so this retries every frame and closes on
      // the first frame painted AFTER the data arrived. That is R2's "data in store → 3D scene
      // painted"; latching unconditionally would have closed it on the first frame after mode entry,
      // reporting a ~one-frame scene regardless of how long the equipment took to draw.
      if (markEnd(EQUIPMENT_PHASES.sceneRender)) {
        sceneMarkedFor.current = loadToken;
      }
    });

    // Diagnostic: count R3F frames. If R3F's frameloop is frozen during a
    // flyTo (the suspected cause of foot-gun #7's "twin stays rendered"
    // symptom), this counter will stay flat while Mapbox repaints underneath.
    // Logs ~once per second to avoid spam.
    const frameCountRef = useRef(0);
    const lastFrameLogRef = useRef(Date.now());
    useFrame(() => {
      frameCountRef.current++;
      const now = Date.now();
      if (now - lastFrameLogRef.current >= 1000) {
        // eslint-disable-next-line no-console
        console.debug(
          '[TwinScene frames] last 1s:',
          frameCountRef.current,
          'twinId=',
          topLevelEntity?.id,
        );
        frameCountRef.current = 0;
        lastFrameLogRef.current = now;
      }
    });

    // poll a dynamically expanded map of models pending loading
    const loadingListRef = useRef<Map<string, boolean>>(new Map<string, boolean>());
    const loadingCompleteRef = useRef(false);
    const currentTwinIdRef = useRef<string | null>(null);
    const completionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [mapInitialized, setMapInitialized] = useState(false);
    const { mapRef } = useTwinContext();
    // The live viewer's height source: a plan view around the scene where one is given, otherwise
    // the Edit flight's (`spec-onboarding-persistent-scene-edit-flight`, R4).
    const outerPlanView = usePlanView();
    const planView = outerPlanView === PLAN_VIEW_OUTSIDE ? liveEditFlightScene : outerPlanView;
    const { setCamera } = useDeveloperStore();

    // Stabilize the onAllModelsLoaded callback to prevent unnecessary re-renders
    const stableOnAllModelsLoaded = useRef(onAllModelsLoaded);
    stableOnAllModelsLoaded.current = onAllModelsLoaded;

    // Reset loading state when twin changes
    useEffect(() => {
      if (currentTwinIdRef.current !== topLevelEntity.id) {
        loadingCompleteRef.current = false;
        // Only clear loading list if we're switching to a completely different twin
        // This prevents clearing when just switching modes or re-rendering
        if (currentTwinIdRef.current !== null) {
          loadingListRef.current.clear();
        }
        if (completionTimeoutRef.current) {
          clearTimeout(completionTimeoutRef.current);
          completionTimeoutRef.current = null;
        }
        currentTwinIdRef.current = topLevelEntity.id;
      }
    }, [topLevelEntity.id]);

    // Optimized model loading completion check
    useEffect(() => {
      const loadingList = loadingListRef.current;
      const originalSet = loadingList.set.bind(loadingList);
      const originalDelete = loadingList.delete.bind(loadingList);

      loadingList.set = function (key: string, value: boolean) {
        const result = originalSet(key, value);

        // Only check completion when a model is marked as loaded
        if (value) {
          const total = Array.from(loadingList.keys()).length;
          const loaded = Array.from(loadingList.values()).filter(Boolean).length;

          // Clear any existing completion timeout and set a new one
          if (loaded === total && loaded > 0 && !loadingCompleteRef.current) {
            // Clear previous timeout if it exists
            if (completionTimeoutRef.current) {
              clearTimeout(completionTimeoutRef.current);
            }

            // Set new timeout - this will be reset if more models load
            completionTimeoutRef.current = setTimeout(() => {
              // Double-check that we're still complete after the delay
              const finalTotal = Array.from(loadingList.keys()).length;
              const finalLoaded = Array.from(loadingList.values()).filter(Boolean).length;

              if (finalLoaded === finalTotal && finalLoaded > 0 && !loadingCompleteRef.current) {
                loadingCompleteRef.current = true;
                stableOnAllModelsLoaded.current();
              }
              completionTimeoutRef.current = null;
            }, MODEL_COMPLETION_DELAY_MS); // Reduced delay to 500ms for better performance
          }
        }

        return result;
      };

      loadingList.delete = function (key: string) {
        return originalDelete(key);
      };

      return () => {
        // Cleanup on unmount - use captured variable
        loadingList.set = originalSet;
        loadingList.delete = originalDelete;
      };
    }, []);

    // Fallback for twins with genuinely no models - only after sufficient time
    useEffect(() => {
      if (mapInitialized && !loadingCompleteRef.current) {
        // Wait a long time to be absolutely sure no models will load
        const fallbackTimer = setTimeout(() => {
          const totalModels = Array.from(loadingListRef.current.keys()).length;
          if (totalModels === 0 && !loadingCompleteRef.current) {
            loadingCompleteRef.current = true;
            stableOnAllModelsLoaded.current();
          }
        }, 10000); // Reduced to 10 seconds for better performance

        return () => clearTimeout(fallbackTimer);
      }
    }, [mapInitialized]);

    // Access the map and its container element from React-Three-Map.
    const map = useMap();
    // Dev-only test hook: expose the map + origin so the Playwright colour-probe
    // harness (tests/twin/colourProbe.pw.spec.ts) can drive the camera into the
    // gym. Stripped from production builds by the NODE_ENV guard.
    if (process.env.NODE_ENV !== 'production') {
      (window as unknown as { __twinMap?: unknown; __twinOrigin?: unknown }).__twinMap = map;
      (window as unknown as { __twinOrigin?: unknown }).__twinOrigin = mapOrigin;
    }
    // Where the live scene is anchored, for the Edit flight's plan-view pose
    // (`spec-onboarding-persistent-scene-edit-flight`, R2.3).
    useEffect(() => {
      setLiveAnchorOrigin(mapOrigin);
      return () => setLiveAnchorOrigin(null);
    }, [mapOrigin]);
    if (!mapRef.current) {
      mapRef.current = map;
    } else if (map !== mapRef.current) {
      mapRef.current = map;
    }

    const mapDiv: HTMLDivElement | null = useMemo(() => {
      return map._canvas.parentElement as HTMLDivElement;
    }, [map]);

    useEffect(() => {
      if (!mapInitialized) {
        // Use the resolved camera configuration from MapCanvas
        const cameraConfig = resolvedCamera || {
          location: mapOrigin,
          camera: DEFAULT_CAMERA_CONFIG,
        };

        // Track the resolved camera in the developer store. This is purely
        // observational — it does not move the actual camera.
        setCamera(
          {
            lat: cameraConfig.location.latitude,
            lng: cameraConfig.location.longitude,
            zoom: cameraConfig.camera.zoom,
            bearing: cameraConfig.camera.bearing,
            pitch: cameraConfig.camera.pitch,
          },
          ConfigurationSource.TWIN_ADMIN,
          'Resolved from unified camera configuration',
        );

        // Camera positioning + bounds-setting were causing visible jump-cuts
        // when twins switched, because this effect runs whenever TwinScene
        // re-mounts (mapInitialized resets). flyToTarget owns camera
        // positioning now (Checkpoint 3); the soft maxBounds get removed
        // entirely in Checkpoint 7 cleanup.

        setMapInitialized(true);
      }
    }, [mapOrigin, mapInitialized, setCamera, resolvedCamera]);

    // CP7 (#5): removed the old camera-store subscription that ran
    // setMaxBounds + map.flyTo on every change. flyToTarget owns every camera
    // move (the store itself went with Edit mode's Twin tab, its only writer,
    // in retire-edit-mode-teardown). The clamp here was the source of the
    // cross-twin clamping bug documented in the architecture doc.

    return (
      <Suspense>
        {/* Always render the scene to prevent disappearing - use opacity for loading state */}
        <group visible={mapInitialized}>
          {/* Warm the GLTF cache once per twin so first view switch has models */}
          {mapInitialized && <PreloadAllModels key={`preload-${topLevelEntity.id}`} />}
          {/* The machine look the height gives (spec-onboarding-persistent-scene-model-look): the
              machines' material, the lights and the occlusion strength. The live viewer draws the
              full-height look at rest, and follows the height source below. */}
          {/* The live viewer's height source (`spec-onboarding-persistent-scene-edit-flight`, R4):
              full height and `off` at rest, exactly the plan view's outside value, so every customer
              sees the same scene; the Edit flight flattens it as it lands. `MapCanvas` provides one
              that reports a launch arrival's growing height while it runs (launch-into-live R4) and
              this Edit flight's otherwise. */}
          <PlanViewContext.Provider value={planView}>
            <MachineLookScope>
              <LiveEditFlightBridge />
              {/* The plan's height group, so a later height source can flatten the models (not the
                lights or effects). */}
              <PlanHeightGroup baseY={0}>
                {loadingListRef.current && (
                  <TwinEntity3D
                    entity={topLevelEntity}
                    mapDiv={mapDiv}
                    loadingList={loadingListRef.current}
                    depth={0}
                    lineage={[]}
                    mapOrigin={mapOrigin}
                  />
                )}
              </PlanHeightGroup>
              <MachineLookLights />
              <MachineLookPostProcessChain />
            </MachineLookScope>
          </PlanViewContext.Provider>
          {Config.appProfile === 'DEBUG' && (
            <>
              <StatsGl />
              <DebugThreeGlInfo infoRef={infoRef} />
            </>
          )}
        </group>
      </Suspense>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if the core twin entity changes - ignore mode/filter changes but include callback changes
    // This prevents calendar view changes, mode changes from affecting the 3D scene
    // but allows callback updates to prevent stale references

    // Check basic entity properties (root level)
    const basicPropsEqual =
      prevProps.twinRootEntity.id === nextProps.twinRootEntity.id &&
      prevProps.mapOrigin.latitude === nextProps.mapOrigin.latitude &&
      prevProps.mapOrigin.longitude === nextProps.mapOrigin.longitude &&
      prevProps.onAllModelsLoaded === nextProps.onAllModelsLoaded &&
      prevProps.resolvedCamera?.location.latitude === nextProps.resolvedCamera?.location.latitude &&
      prevProps.resolvedCamera?.location.longitude === nextProps.resolvedCamera?.location.longitude;

    if (!basicPropsEqual) {
      return false; // Props have changed, should re-render
    }

    // Use fingerprint comparison instead of multiple tree traversals
    // This is significantly more efficient for large entity trees as it:
    // 1. Performs a single traversal instead of three separate ones
    // 2. Compares a single string instead of multiple arrays
    // 3. Reduces memory allocations from intermediate arrays
    const prevFingerprint = computeEntityTreeFingerprint(prevProps.twinRootEntity);
    const nextFingerprint = computeEntityTreeFingerprint(nextProps.twinRootEntity);

    // If fingerprints match, all relevant properties (modelFiles, rotations, name, capacity) are unchanged
    if (prevFingerprint !== nextFingerprint) {
      return false; // Entity tree has changed, should re-render
    }

    // Return true (skip re-render) only if all props are equal
    return true;
  },
);

export { TwinScene };
